// ProjectInfo.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import EditProjectModal from "./EditProjectModal";
import Icon22 from "../img/efd2a7af2d5ebcce4b20e5c9794b3ea23005e284d3041cbac31c48228ed046a4.svg";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { ReactComponent as AttachmentAfter } from "../Attachment-After.svg";
import { ReactComponent as AttachmentBefore } from "../Attachment-Before.svg";
import { fetchWithRetry, debounce } from "../utils/graphApi";
import { useAuthFetch } from "../hooks/useAuthFetch";

/** ===== SharePoint / Graph constants (scoped in components where used) ===== */
const SHAREPOINT_HOSTNAME = "westernfireproofing.sharepoint.com";
const SHAREPOINT_SITE_PATH = "/sites/ProjectDatabase";

// Hardcoded drive IDs for specific document libraries
const DRIVE_IDS = {
  Compliance: 'b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKUV8BOuqoSQQJw3J8dZP1_u',
  Contracts: 'b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKVlBwV8Mr_nQJKvl_rlJHqj',
  'Insurance & Bonds': 'b!4i1ca56UUkiE0AE60mmSgHzRvOe8JzBLgemiibQ4FKXuiMvMdFvuR5IZBSZmVE4a',
};

// SharePoint strips special characters (e.g. "&") from a library's URL segment
// even though the display name keeps them, so the real URL can differ from the
// label. Map display name -> actual URL segment so "Open in SharePoint" links
// resolve instead of 404ing. (Same mapping used in DocManagementLayout.jsx.)
const LIBRARY_URL_SEGMENTS = {
  'Insurance & Bonds': 'Insurance  Bonds',
};
const toLibraryUrlSegment = (name) => LIBRARY_URL_SEGMENTS[name] || name;

/** ===== Small inline icons (no extra deps) ===== */
const PencilIcon = (props) => (
  <svg className={`w-4 h-4 ${props.className || ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

// Indicates no files: just a clip
const PaperClipIcon = (props) => (
  <AttachmentBefore width="15" height="20" {...props} />
);

const PaperWithClipIcon = (props) => (
  <AttachmentAfter width="15" height="20" {...props} />
);


// LinkOutIcon unused here; keep inline in JSX where needed

/** ===== ContractFilesModal (unchanged behavior; for Contracts/{projectId}) ===== */
function ContractFilesModal({ isOpen, onClose, projectData }) {
  const { instance, accounts } = useMsal();

  const folderKey = useMemo(
    () => projectData?.projectId || projectData?.salesforceProjectId || projectData?.id || "Unknown",
    [projectData]
  );

  const SHAREPOINT_BASE_URL = `https://${SHAREPOINT_HOSTNAME}${SHAREPOINT_SITE_PATH}`;
  const CONTRACTS_LIBRARY_NAME = "Contracts"; // << use the library, not a folder

  // Link to the library folder in SharePoint UI
  const sharepointFolderUrl = useMemo(() => {
    const idPath = `/sites/ProjectDatabase/${CONTRACTS_LIBRARY_NAME}/${folderKey}`;
    return `${SHAREPOINT_BASE_URL}/${CONTRACTS_LIBRARY_NAME}/Forms/AllItems.aspx?id=${encodeURIComponent(idPath)}`;
  }, [folderKey, SHAREPOINT_BASE_URL, CONTRACTS_LIBRARY_NAME]);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const siteIdRef = useRef(null);
  const contractsDriveIdRef = useRef(null);

  const SCOPES = useMemo(() => {
    const base = (loginRequest && loginRequest.scopes) ? loginRequest.scopes : [];
    return Array.from(new Set([...base, "Files.ReadWrite.All", "Sites.Read.All", "Sites.ReadWrite.All"]));
  }, []);

  const getToken = useCallback(async () => {
    try {
      const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0], scopes: SCOPES });
      return res.accessToken;
    } catch {
      const res = await instance.loginPopup({ ...loginRequest, scopes: SCOPES });
      return res.accessToken;
    }
  }, [instance, accounts, SCOPES]);

  const ensureSiteId = useCallback(async (token) => {
    if (siteIdRef.current) return siteIdRef.current;

    // Try to read from localStorage
    const cached = localStorage.getItem("siteId");
    if (cached) {
      siteIdRef.current = cached;
      return cached;
    }

    // Fetch with retry logic
    const resp = await fetchWithRetry(
      `https://graph.microsoft.com/v1.0/sites/${SHAREPOINT_HOSTNAME}:${SHAREPOINT_SITE_PATH}?$select=id`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!resp.ok) throw new Error(`Graph(site id) HTTP ${resp.status}`);

    const json = await resp.json();

    //Save in ref
    siteIdRef.current = json.id;

    //Save in localStorage
    localStorage.setItem("siteId", json.id);

    return json.id;
  }, []);

  // find the Contracts **drive** (document library)
  const ensureContractsDriveId = useCallback(async (token, siteId) => {

    // If already cached in ref, return
    if (contractsDriveIdRef.current) return contractsDriveIdRef.current;

    // Use hardcoded driveId if provided
    if (DRIVE_IDS[CONTRACTS_LIBRARY_NAME]) {
      contractsDriveIdRef.current = DRIVE_IDS[CONTRACTS_LIBRARY_NAME];
      return contractsDriveIdRef.current;
    }

    // Try localStorage
    const cached = localStorage.getItem("contractsDriveId");
    if (cached) {
      contractsDriveIdRef.current = cached;
      return cached;
    }

    // Fetch drives with retry logic
    const resp = await fetchWithRetry(
      // const resp = await fetch(
      `https://graph.microsoft.com/v1.0/sites/${siteId}/drives?$select=id,name,driveType`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (!resp.ok) throw new Error(`Graph(drives) HTTP ${resp.status}`);

    const json = await resp.json();

    // Find CONTRACTS library
    const contracts = (json.value || []).find(
      d => (d.driveType === "documentLibrary") && (d.name || "").toLowerCase() === CONTRACTS_LIBRARY_NAME.toLowerCase()
    );

    if (!contracts) throw new Error(`Document library '${CONTRACTS_LIBRARY_NAME}' not found`);

    //Cache in ref + localStorage
    contractsDriveIdRef.current = contracts.id;
    localStorage.setItem("contractsDriveId", contracts.id);

    return contracts.id;

  }, []);

  const listFiles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      const siteId = await ensureSiteId(token);
      const driveId = await ensureContractsDriveId(token, siteId);

      // List children of Contracts/{projectId}
      const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/` +
        `${encodeURIComponent(folderKey)}:/children?` +
        `$select=name,webUrl,lastModifiedDateTime,size`;
      const resp = await fetchWithRetry(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!resp || resp.status === 404) {
        // Folder does not exist → treat as empty
        setFiles([]);
        return;
      }

      if (!resp.ok) {
        console.error("Graph list failed", resp.status);
        setFiles([]);
        return;
      }

      const json = await resp.json();
      setFiles(Array.isArray(json.value) ? json.value : []);
    } catch (e) {
      // setError(e.message || "Failed to load files");
      console.warn("File list error (ignored)", e);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [folderKey, getToken, ensureSiteId, ensureContractsDriveId]);

  // Ensure /Contracts/{projectId} exists (inside the Contracts library)
  const ensureFolderPath = useCallback(async (token, driveId) => {
    // Check if folder exists
    const check = await fetch(
      `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${encodeURIComponent(folderKey)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (check.ok) return;

    // Create it if missing
    const create = await fetch(
      `https://graph.microsoft.com/v1.0/drives/${driveId}/root/children`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: folderKey,
          folder: {},
          "@microsoft.graph.conflictBehavior": "replace",
        }),
      }
    );
    if (!create.ok) throw new Error(`Create folder failed: HTTP ${create.status}`);
  }, [folderKey]);

  const simpleUpload = useCallback(async (token, driveId, file) => {
    const putUrl =
      `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/` +
      `${encodeURIComponent(folderKey)}/${encodeURIComponent(file.name)}:/content`;
    const resp = await fetch(putUrl, { method: "PUT", headers: { Authorization: `Bearer ${token}` }, body: file });
    if (!resp.ok) throw new Error(`Upload failed: HTTP ${resp.status}`);
  }, [folderKey]);

  const largeUpload = useCallback(async (token, driveId, file) => {
    const sessionUrl =
      `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/` +
      `${encodeURIComponent(folderKey)}/${encodeURIComponent(file.name)}:/createUploadSession`;

    const sessionResp = await fetch(sessionUrl, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ item: { "@microsoft.graph.conflictBehavior": "replace", name: file.name } }),
    });
    if (!sessionResp.ok) throw new Error(`Create upload session failed: HTTP ${sessionResp.status}`);
    const { uploadUrl } = await sessionResp.json();

    const chunkSize = 5 * 1024 * 1024;
    let start = 0;
    while (start < file.size) {
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      const res = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Range": `bytes ${start}-${end - 1}/${file.size}` },
        body: chunk,
      });
      if (![200, 201, 202].includes(res.status)) throw new Error(`Chunk upload failed: HTTP ${res.status}`);
      start = end;
    }
  }, [folderKey]);

  const uploadFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    setError("");
    try {
      const token = await getToken();
      const siteId = await ensureSiteId(token);
      const driveId = await ensureContractsDriveId(token, siteId);
      await ensureFolderPath(token, driveId);

      for (const f of Array.from(fileList)) {
        if (f.size <= 4 * 1024 * 1024) await simpleUpload(token, driveId, f);
        else await largeUpload(token, driveId, f);
      }
      await listFiles();
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }, [getToken, ensureSiteId, ensureContractsDriveId, ensureFolderPath, largeUpload, listFiles, simpleUpload]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  }, [uploadFiles]);

  const fileInputRef = useRef(null);
  useEffect(() => { if (isOpen) listFiles(); }, [isOpen, listFiles]);
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10070] flex items-center justify-center bg-black/40 p-4" onDragOver={(e) => { e.preventDefault(); }} onDrop={onDrop}>
      <div className="w-[820px] max-w-[95vw] max-h-[90dvh] overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="text-lg font-semibold">Project Database &gt; Contract &gt; {folderKey}</div>
          <button onClick={onClose} className="rounded-md p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100" title="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-600">
              Files inside <span className="font-medium">{CONTRACTS_LIBRARY_NAME}/{folderKey}</span>
            </div>
            <a href={sharepointFolderUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-red-700 hover:underline" title="Open in SharePoint">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 3h7m0 0v7m0-7L10 14M5 10v10a1 1 0 001 1h10" /></svg>
              Open in SharePoint
            </a>
          </div>

          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 flex">
              <div className="w-2/3">Name</div>
              <div className="w-1/3">Modified</div>
            </div>
            <div className="max-h-64 overflow-auto divide-y">
              {loading ? (
                <div className="px-4 py-6 text-sm text-gray-500">Loading…</div>
              ) : files.length === 0 ? (
                <div className="px-4 py-6 text-sm text-gray-500">No files yet.</div>
              ) : (
                files.map(f => (
                  <div key={f.webUrl || f.name} className="px-4 py-3 text-sm flex">
                    <div className="w-2/3 truncate">
                      <a className="text-red-700 hover:underline" href={f.webUrl} target="_blank" rel="noreferrer">{f.name}</a>
                    </div>
                    <div className="w-1/3 text-gray-600">
                      {f.lastModifiedDateTime ? new Date(f.lastModifiedDateTime).toLocaleString() : "-"}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div
            className="mt-6 h-40 rounded-md border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500"
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
            onDrop={onDrop}
          >
            <div className="mb-2">Drag and drop files here</div>
            <div className="text-xs mb-3">or</div>
            <button className="px-3 py-1.5 rounded bg-red-700 text-white text-sm hover:bg-red-800" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {uploading ? "Uploading…" : "Choose files"}
            </button>
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
          </div>
          {error && <div className="mt-4 text-sm text-red-600">Error: {error}</div>}
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded bg-gray-100 hover:bg-gray-200">Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** ===== Generic LibraryFilesModal for subfolders (Compliance / Insurance & Bonds) =====
 *  Opens: Compliance/{projectId}/Tax Exempt
 *         Compliance/{projectId}/Prevailing Wage
 *         Insurance & Bonds/{projectId}/P&P Bonds
 */
function LibraryFilesModal({ isOpen, onClose, projectData, libraryName, subPath }) {
  const { instance, accounts } = useMsal();
  const folderKey = useMemo(
    () => projectData?.projectId || projectData?.salesforceProjectId || projectData?.id || "Unknown",
    [projectData]
  );

  const SHAREPOINT_BASE_URL = `https://${SHAREPOINT_HOSTNAME}${SHAREPOINT_SITE_PATH}`;

  const titleCrumb = useMemo(() => {
    return `Project Database > ${libraryName} > ${folderKey}${subPath ? ` > ${subPath}` : ""}`;
  }, [libraryName, folderKey, subPath]);

  const sharepointFolderUrl = useMemo(() => {
    const librarySegment = toLibraryUrlSegment(libraryName);
    const idParts = [`/sites/ProjectDatabase/${librarySegment}`, folderKey];
    if (subPath) idParts.push(subPath);
    const idPath = idParts.join("/");
    return `${SHAREPOINT_BASE_URL}/${encodeURIComponent(librarySegment)}/Forms/AllItems.aspx?id=${encodeURIComponent(idPath)}`;
  }, [libraryName, folderKey, subPath, SHAREPOINT_BASE_URL]);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const siteIdRef = useRef(null);
  const driveIdRef = useRef(null);

  const SCOPES = useMemo(() => {
    const base = (loginRequest && loginRequest.scopes) ? loginRequest.scopes : [];
    return Array.from(new Set([...base, "Files.ReadWrite.All", "Sites.Read.All", "Sites.ReadWrite.All"]));
  }, []);

  const getToken = useCallback(async () => {
    try {
      const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts?.[0], scopes: SCOPES });
      return res.accessToken;
    } catch {
      const res = await instance.loginPopup({ ...loginRequest, scopes: SCOPES });
      return res.accessToken;
    }
  }, [instance, accounts, SCOPES]);

  const ensureSiteId = useCallback(async (token) => {

    // If already in ref → return immediately
    if (siteIdRef.current) return siteIdRef.current;

    // Check localStorage cache
    const cached = localStorage.getItem("siteId");
    if (cached) {
      siteIdRef.current = cached;
      return cached;
    }

    // Call Graph with retry logic (prevents 429 throttling)
    const resp = await fetchWithRetry(
      // const resp = await fetch(
      `https://graph.microsoft.com/v1.0/sites/${SHAREPOINT_HOSTNAME}:${SHAREPOINT_SITE_PATH}?$select=id`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (!resp.ok) throw new Error(`Graph(site id) HTTP ${resp.status}`);

    const json = await resp.json();

    if (!json.id) throw new Error("SiteId not found in Graph response");

    // Cache in ref + localStorage
    siteIdRef.current = json.id;
    localStorage.setItem("siteId", json.id);

    return json.id;
  }, []);

  const ensureDriveId = useCallback(async (token, siteId, libName) => {
    if (driveIdRef.current) return driveIdRef.current;

    // Use hardcoded driveId if available for this library
    if (DRIVE_IDS[libName]) {
      driveIdRef.current = DRIVE_IDS[libName];
      return driveIdRef.current;
    }
    const resp = await fetch(
      `https://graph.microsoft.com/v1.0/sites/${siteId}/drives?$select=id,name,driveType`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!resp.ok) throw new Error(`Graph(drives) HTTP ${resp.status}`);
    const json = await resp.json();
    const found = (json.value || []).find(
      d => (d.driveType === "documentLibrary") && (d.name || "").toLowerCase() === libName.toLowerCase()
    );
    if (!found) throw new Error(`Document library '${libName}' not found`);
    driveIdRef.current = found.id;
    return found.id;
  }, []);

  const encoded = (s) => encodeURIComponent(s);

  const listFiles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      const siteId = await ensureSiteId(token);
      const driveId = await ensureDriveId(token, siteId, libraryName);

      const basePath = subPath
        ? `${encoded(folderKey)}/${encoded(subPath)}`
        : `${encoded(folderKey)}`;

      const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${basePath}:/children?$select=name,webUrl,lastModifiedDateTime,size`;
      const resp = await fetchWithRetry(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!resp || resp.status === 404) {
        // Folder or subfolder does not exist yet
        setFiles([]);
        return;
      }

      if (!resp.ok) {
        console.error("Graph list failed", resp.status);
        setFiles([]);
        return;
      }

      const json = await resp.json();
      setFiles(Array.isArray(json.value) ? json.value : []);
    } catch (e) {
      // setError(e.message || "Failed to load files");
      console.warn("File list error (ignored)", e);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [folderKey, subPath, libraryName, getToken, ensureSiteId, ensureDriveId]);

  const ensureFolderPath = useCallback(async (token, driveId) => {
    // Ensure {projectId} exists
    const projectCheck = await fetch(
      `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${encodeURIComponent(folderKey)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!projectCheck.ok) {
      const createProject = await fetch(
        `https://graph.microsoft.com/v1.0/drives/${driveId}/root/children`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            name: folderKey,
            folder: {},
            "@microsoft.graph.conflictBehavior": "replace",
          }),
        }
      );
      if (!createProject.ok) throw new Error(`Create project folder failed: HTTP ${createProject.status}`);
    }

    // Ensure subPath exists (if specified)
    if (subPath) {
      const subCheck = await fetch(
        `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${encodeURIComponent(folderKey)}/${encodeURIComponent(subPath)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!subCheck.ok) {
        const createSub = await fetch(
          `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${encodeURIComponent(folderKey)}:/children`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({
              name: subPath,
              folder: {},
              "@microsoft.graph.conflictBehavior": "replace",
            }),
          }
        );
        if (!createSub.ok) throw new Error(`Create subfolder failed: HTTP ${createSub.status}`);
      }
    }
  }, [folderKey, subPath]);

  const simpleUpload = useCallback(async (token, driveId, file) => {
    const relPath = subPath
      ? `${encodeURIComponent(folderKey)}/${encodeURIComponent(subPath)}/${encodeURIComponent(file.name)}`
      : `${encodeURIComponent(folderKey)}/${encodeURIComponent(file.name)}`;

    const putUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${relPath}:/content`;
    const resp = await fetch(putUrl, { method: "PUT", headers: { Authorization: `Bearer ${token}` }, body: file });
    if (!resp.ok) throw new Error(`Upload failed: HTTP ${resp.status}`);
  }, [folderKey, subPath]);

  const largeUpload = useCallback(async (token, driveId, file) => {
    const relPath = subPath
      ? `${encodeURIComponent(folderKey)}/${encodeURIComponent(subPath)}/${encodeURIComponent(file.name)}`
      : `${encodeURIComponent(folderKey)}/${encodeURIComponent(file.name)}`;

    const sessionUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${relPath}:/createUploadSession`;

    const sessionResp = await fetch(sessionUrl, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ item: { "@microsoft.graph.conflictBehavior": "replace", name: file.name } }),
    });
    if (!sessionResp.ok) throw new Error(`Create upload session failed: HTTP ${sessionResp.status}`);
    const { uploadUrl } = await sessionResp.json();

    const chunkSize = 5 * 1024 * 1024;
    let start = 0;
    while (start < file.size) {
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      const res = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Range": `bytes ${start}-${end - 1}/${file.size}` },
        body: chunk,
      });
      if (![200, 201, 202].includes(res.status)) throw new Error(`Chunk upload failed: HTTP ${res.status}`);
      start = end;
    }
  }, [folderKey, subPath]);

  const uploadFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    setError("");
    try {
      const token = await getToken();
      const siteId = await ensureSiteId(token);
      const driveId = await ensureDriveId(token, siteId, libraryName);
      await ensureFolderPath(token, driveId);

      for (const f of Array.from(fileList)) {
        if (f.size <= 4 * 1024 * 1024) await simpleUpload(token, driveId, f);
        else await largeUpload(token, driveId, f);
      }
      await listFiles();
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }, [libraryName, getToken, ensureSiteId, ensureDriveId, ensureFolderPath, largeUpload, listFiles, simpleUpload]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  }, [uploadFiles]);

  const fileInputRef = useRef(null);
  useEffect(() => { if (isOpen) listFiles(); }, [isOpen, listFiles]);
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10070] flex items-center justify-center bg-black/40 p-4" onDragOver={(e) => { e.preventDefault(); }} onDrop={onDrop}>
      <div className="w-[820px] max-w-[95vw] max-h-[90dvh] overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="text-lg font-semibold">{titleCrumb}</div>
          <button onClick={onClose} className="rounded-md p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100" title="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-600">
              Files inside <span className="font-medium">{libraryName}/{folderKey}{subPath ? `/${subPath}` : ""}</span>
            </div>
            <a href={sharepointFolderUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-red-700 hover:underline" title="Open in SharePoint">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 3h7m0 0v7m0-7L10 14M5 10v10a1 1 0 001 1h10" /></svg>
              Open in SharePoint
            </a>
          </div>

          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 flex">
              <div className="w-2/3">Name</div>
              <div className="w-1/3">Modified</div>
            </div>
            <div className="max-h-64 overflow-auto divide-y">
              {loading ? (
                <div className="px-4 py-6 text-sm text-gray-500">Loading…</div>
              ) : files.length === 0 ? (
                <div className="px-4 py-6 text-sm text-gray-500">No files yet.</div>
              ) : (
                files.map(f => (
                  <div key={f.webUrl || f.name} className="px-4 py-3 text-sm flex">
                    <div className="w-2/3 truncate">
                      <a className="text-red-700 hover:underline" href={f.webUrl} target="_blank" rel="noreferrer">{f.name}</a>
                    </div>
                    <div className="w-1/3 text-gray-600">
                      {f.lastModifiedDateTime ? new Date(f.lastModifiedDateTime).toLocaleString() : "-"}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div
            className="mt-6 h-40 rounded-md border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500"
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
            onDrop={onDrop}
          >
            <div className="mb-2">Drag and drop files here</div>
            <div className="text-xs mb-3">or</div>
            <button className="px-3 py-1.5 rounded bg-red-700 text-white text-sm hover:bg-red-800" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {uploading ? "Uploading…" : "Choose files"}
            </button>
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
          </div>
          {error && <div className="mt-4 text-sm text-red-600">Error: {error}</div>}
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded bg-gray-100 hover:bg-gray-200">Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** ===== Main ProjectInfo component (with file buttons for 4 fields) ===== */
function ProjectInfo() {
  const { authFetch, isReady } = useAuthFetch();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isFilesOpen, setIsFilesOpen] = useState(false); // Contract modal
  // New modals:
  const [isTaxExemptOpen, setIsTaxExemptOpen] = useState(false);
  const [isPrevailingWageOpen, setIsPrevailingWageOpen] = useState(false);
  const [isPPBondsOpen, setIsPPBondsOpen] = useState(false);

  const { instance, accounts } = useMsal();

  // Helper function to get value from either camelCase or snake_case
  const getFieldValue = useCallback((data, camelCase, snakeCase) => {
    return data[camelCase] ?? data[snakeCase] ?? '';
  }, []);

  // Use state instead of memo so it can be updated
  const [projectData, setProjectData] = useState(() => {
    let data = JSON.parse(localStorage.getItem('selectedProjectDetails')) || {};
    // Check if data is wrapped in "records" object
    if (data.records) {
      data = { ...data, ...data.records };
    }
    return data;
  });

  // Listen for project details changes
  useEffect(() => {
    const handleProjectChange = (event) => {
      let data = event.detail || JSON.parse(localStorage.getItem('selectedProjectDetails')) || {};
      // Check if data is wrapped in "records" object
      if (data.records) {
        data = { ...data, ...data.records };
      }
      setProjectData(data);
    };

    window.addEventListener('projectDetailsChanged', handleProjectChange);

    // Also check localStorage on mount
    let initialData = JSON.parse(localStorage.getItem('selectedProjectDetails')) || {};

    // Check if data is wrapped in "records" object
    if (initialData.records) {
      console.log('ProjectInfo - Found data in records wrapper, unwrapping...');
      initialData = { ...initialData, ...initialData.records };
    }

    console.log('ProjectInfo - Loaded project data:', {
      prevailingWage: initialData.prevailingWage,
      prevailing_wage: initialData.prevailing_wage,
      retainage: initialData.retainage,
      ppBonds: initialData.ppBonds,
      p_and_p_bonds: initialData.p_and_p_bonds,
      billingDate: initialData.billingDate,
      bill_date: initialData.bill_date,
      allData: initialData
    });
    setProjectData(initialData);

    return () => {
      window.removeEventListener('projectDetailsChanged', handleProjectChange);
    };
  }, []);

  // Determine folder key used in libraries
  const folderKey = useMemo(() => (
    projectData?.projectId || projectData?.salesforceProjectId || projectData?.id || "Unknown"
  ), [projectData.projectId, projectData.salesforceProjectId, projectData.id]);

  // file-presence indicators (for paperclip vs clip)
  const [hasContractFiles, setHasContractFiles] = useState(false);
  const [hasTaxExemptFiles, setHasTaxExemptFiles] = useState(false);
  const [hasPrevailingWageFiles, setHasPrevailingWageFiles] = useState(false);
  const [hasPPBondsFiles, setHasPPBondsFiles] = useState(false);

  const siteIdRef = useRef(null);
  const driveCacheRef = useRef({}); // cache driveId by library name

  const SCOPES = useMemo(() => {
    const base = (loginRequest && loginRequest.scopes) ? loginRequest.scopes : [];
    return Array.from(new Set([...base, "Files.ReadWrite.All", "Sites.Read.All", "Sites.ReadWrite.All"]));
  }, []);

  const getToken = useCallback(async () => {
    try {
      const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts?.[0], scopes: SCOPES });
      return res.accessToken;
    } catch {
      const res = await instance.loginPopup({ ...loginRequest, scopes: SCOPES });
      return res.accessToken;
    }
  }, [instance, accounts, SCOPES]);

  const ensureSiteId = useCallback(async (token) => {
    if (siteIdRef.current) return siteIdRef.current;
    const resp = await fetch(
      `https://graph.microsoft.com/v1.0/sites/${SHAREPOINT_HOSTNAME}:${SHAREPOINT_SITE_PATH}?$select=id`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!resp.ok) throw new Error(`Graph(site id) HTTP ${resp.status}`);
    const json = await resp.json();
    siteIdRef.current = json.id;
    return json.id;
  }, []);

  const ensureDriveId = useCallback(async (token, siteId, libraryName) => {
    if (driveCacheRef.current[libraryName]) return driveCacheRef.current[libraryName];

    // Use hardcoded driveId if available
    if (DRIVE_IDS[libraryName]) {
      driveCacheRef.current[libraryName] = DRIVE_IDS[libraryName];
      return driveCacheRef.current[libraryName];
    }
    const resp = await fetch(
      `https://graph.microsoft.com/v1.0/sites/${siteId}/drives?$select=id,name,driveType`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!resp.ok) throw new Error(`Graph(drives) HTTP ${resp.status}`);
    const json = await resp.json();
    const found = (json.value || []).find(
      d => (d.driveType === "documentLibrary") && (d.name || "").toLowerCase() === libraryName.toLowerCase()
    );
    if (!found) throw new Error(`Library not found: ${libraryName}`);
    driveCacheRef.current[libraryName] = found.id;
    return found.id;
  }, []);

  // ----- checkers for presence of files -----
  const checkHasFilesInPath = useCallback(async (libraryName, subPath, setter) => {
    try {
      const token = await getToken();
      const siteId = await ensureSiteId(token);
      const driveId = await ensureDriveId(token, siteId, libraryName);

      const path = subPath
        ? `${encodeURIComponent(folderKey)}/${encodeURIComponent(subPath)}`
        : `${encodeURIComponent(folderKey)}`;

      const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${path}:/children?$select=id`;
      const resp = await fetchWithRetry(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp) { setter(false); return; } // Retries exhausted or null response
      if (resp.status === 404) { setter(false); return; }
      if (!resp.ok) throw new Error("list children failed");
      const json = await resp.json();
      setter(Array.isArray(json.value) && json.value.length > 0);
    } catch {
      setter(false);
    }
  }, [folderKey, getToken, ensureSiteId, ensureDriveId]);

  const checkHasContractFiles = useCallback(async () => {
    await checkHasFilesInPath("Contracts", null, setHasContractFiles);
  }, [checkHasFilesInPath]);

  const checkHasTaxExemptFiles = useCallback(async () => {
    await checkHasFilesInPath("Compliance", "Tax Exempt", setHasTaxExemptFiles);
  }, [checkHasFilesInPath]);

  const checkHasPrevailingWageFiles = useCallback(async () => {
    await checkHasFilesInPath("Compliance", "Prevailing Wage", setHasPrevailingWageFiles);
  }, [checkHasFilesInPath]);

  const checkHasPPBondsFiles = useCallback(async () => {
    await checkHasFilesInPath("Insurance & Bonds", "P&P Bonds", setHasPPBondsFiles);
  }, [checkHasFilesInPath]);

  // Track which checks have been performed to prevent duplicate calls
  const checksPerformedRef = useRef(new Set());
  const checkTimeoutRef = useRef({});

  // initial checks - staggered to avoid concurrent requests
  useEffect(() => {
    const checkKey = `initial-${folderKey}`;
    if (checksPerformedRef.current.has(checkKey)) return;
    
    checksPerformedRef.current.add(checkKey);
    
    // Stagger the checks to avoid concurrent requests (1 second between each)
    setTimeout(() => checkHasContractFiles(), 0);
    setTimeout(() => checkHasTaxExemptFiles(), 1000);
    setTimeout(() => checkHasPrevailingWageFiles(), 2000);
    setTimeout(() => checkHasPPBondsFiles(), 3000);
  }, [folderKey]); // Only depend on folderKey, not the callback functions

  // refresh after modals close - debounced to prevent rapid successive calls
  const debouncedCheckContract = useMemo(
    () => debounce(() => checkHasContractFiles(), 2000),
    [checkHasContractFiles]
  );
  const debouncedCheckTaxExempt = useMemo(
    () => debounce(() => checkHasTaxExemptFiles(), 2000),
    [checkHasTaxExemptFiles]
  );
  const debouncedCheckPrevailingWage = useMemo(
    () => debounce(() => checkHasPrevailingWageFiles(), 2000),
    [checkHasPrevailingWageFiles]
  );
  const debouncedCheckPPBonds = useMemo(
    () => debounce(() => checkHasPPBondsFiles(), 2000),
    [checkHasPPBondsFiles]
  );

  useEffect(() => {
    if (!isFilesOpen) {
      debouncedCheckContract();
    }
    return () => {
      // Cleanup on unmount
    };
  }, [isFilesOpen, debouncedCheckContract]);

  useEffect(() => {
    if (!isTaxExemptOpen) {
      debouncedCheckTaxExempt();
    }
  }, [isTaxExemptOpen, debouncedCheckTaxExempt]);

  useEffect(() => {
    if (!isPrevailingWageOpen) {
      debouncedCheckPrevailingWage();
    }
  }, [isPrevailingWageOpen, debouncedCheckPrevailingWage]);

  useEffect(() => {
    if (!isPPBondsOpen) {
      debouncedCheckPPBonds();
    }
  }, [isPPBondsOpen, debouncedCheckPPBonds]);

  const formatAddress = () => {
    const street = projectData.street || '';
    const city = projectData.city || '';
    const state = projectData.state || '';
    const zipCode = projectData.zipCode || '';

    if (street || city || state || zipCode) {
      const firstLine = street;
      const secondLine = [city, state, zipCode].filter(Boolean).join(', ');
      if (firstLine && secondLine) {
        return (
          <div>
            <div className="truncate" title={firstLine}>{firstLine}</div>
            <div className="truncate" title={secondLine}>{secondLine}</div>
          </div>
        );
      } else if (secondLine) {
        return <div className="truncate" title={secondLine}>{secondLine}</div>;
      }
    }

    if (projectData.projectAddress && projectData.projectAddress !== '-') {
      const parts = projectData.projectAddress.split(', ');
      if (parts.length >= 3) {
        const street = parts[0];
        const cityStateZip = parts.slice(1).join(', ');
        return (
          <div>
            <div className="truncate" title={street}>{street}</div>
            <div className="truncate" title={cityStateZip}>{cityStateZip}</div>
          </div>
        );
      }
      return <div className="truncate" title={projectData.projectAddress}>{projectData.projectAddress}</div>;
    }
    return '-';
  };

  const formatCustomerAddress = () => {
    const street = projectData.customerStreet || '';
    const city = projectData.customerCity || '';
    const state = projectData.customerState || '';
    const zipCode = projectData.customerZipCode || '';

    if (street || city || state || zipCode) {
      const firstLine = street;
      const secondLine = [city, state, zipCode].filter(Boolean).join(', ');
      if (firstLine && secondLine) {
        return (
          <div>
            <div className="truncate" title={firstLine}>{firstLine}</div>
            <div className="truncate" title={secondLine}>{secondLine}</div>
          </div>
        );
      } else if (secondLine) {
        return <div className="truncate" title={secondLine}>{secondLine}</div>;
      }
    }
    return '-';
  };

  const billingDateOptions = ["10th", "15th", "20th", "25th", "30th", "Upon Completion"];
  const contractOptions = ["Executed Proposal", "Executed", "Received LOI", "Signed Proposal", "Required", "Sent", "Service"];
  const taxExemptOptions = ["Taxable", "Req'd Not Received", "Received", "Required"];
  const yesNoOptions = ["Yes", "No"];
  const retainageOptions = ["0%", "5%", "10%"];

  const [editingField, setEditingField] = useState(null);
  const [editValues, setEditValues] = useState({});

  const handleEditClick = (fieldName) => {
    setEditingField(fieldName);
    // Map fieldName to both camelCase and snake_case for getting current value
    const fieldMapping = {
      'billingDate': ['billingDate', 'bill_date'],
      'taxExempt': ['taxExempt', 'tax_exempt'],
      'certifiedPayroll': ['certifiedPayroll', 'certified_payroll'],
      'ppBonds': ['ppBonds', 'p_and_p_bonds'],
      'prevailingWage': ['prevailingWage', 'prevailing_wage'],
      'retainage': ['retainage', 'retainage'],
      'ccip': ['ccip', 'ccip'],
      'contract': ['contract', 'contract']
    };
    const [camelCase, snakeCase] = fieldMapping[fieldName] || [fieldName, fieldName];
    const currentValue = getFieldValue(projectData, camelCase, snakeCase);
    setEditValues({ ...editValues, [fieldName]: currentValue });
  };

  const handleSaveEdit = async (fieldName) => {
    if (!isReady) return;
    try {
      const newValue = editValues[fieldName];
      // Save in both camelCase and snake_case formats for compatibility
      const updatedData = { ...projectData, [fieldName]: newValue };

      // Also update the snake_case version in localStorage
      const snakeCaseMapping = {
        'billingDate': 'bill_date',
        'taxExempt': 'tax_exempt',
        'certifiedPayroll': 'certified_payroll',
        'ppBonds': 'p_and_p_bonds',
        'prevailingWage': 'prevailing_wage',
        'retainage': 'retainage',
        'ccip': 'ccip',
        'contract': 'contract'
      };

      if (snakeCaseMapping[fieldName]) {
        updatedData[snakeCaseMapping[fieldName]] = newValue;
      }

      localStorage.setItem('selectedProjectDetails', JSON.stringify(updatedData));

      if (projectData.salesforceProjectId) {
        const requestBody = {};
        switch (fieldName) {
          case 'billingDate': requestBody.bill_date = newValue; break;
          case 'contract': requestBody.contract = newValue; break;
          case 'taxExempt': requestBody.tax_exempt = newValue; break;
          case 'certifiedPayroll': requestBody.certified_payroll = newValue; break;
          case 'ppBonds': requestBody.p_and_p_bonds = newValue; break;
          case 'ccip': requestBody.ccip = newValue; break;
          case 'retainage': requestBody.retainage = newValue; break;
          case 'prevailingWage': requestBody.prevailing_wage = newValue; break;
          default: break;
        }

        if (Object.keys(requestBody).length > 0) {
          const response = await authFetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/project/${projectData.salesforceProjectId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ project_id: projectData.salesforceProjectId, ...requestBody })
          });
          if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
        }
      }

      window.dispatchEvent(new CustomEvent('projectDetailsChanged', { detail: updatedData }));
      setEditingField(null);
      setEditValues({});
    } catch (error) {
      console.error('Error saving field:', error);
      alert(`Failed to save ${fieldName}: ${error.message}`);
    }
  };

  const handleCancelEdit = () => { setEditingField(null); setEditValues({}); };
  const handleDropdownChange = (fieldName, value) => setEditValues({ ...editValues, [fieldName]: value });

  const renderEditableField = (fieldName, label, options, currentValue) => {
    const isEditing = editingField === fieldName;

    // Debug logging
    if (fieldName === 'prevailingWage' || fieldName === 'retainage' || fieldName === 'ppBonds') {
      console.log(`renderEditableField - ${fieldName}:`, currentValue);
    }

    if (isEditing) {
      return (
        <div className="flex items-center justify-between w-full">
          <select
            value={editValues[fieldName] || ""}
            onChange={(e) => handleDropdownChange(fieldName, e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-red-500 w-[200px]"
          >
            <option value="">Select</option>
            {options.map(option => (<option key={option} value={option}>{option}</option>))}
          </select>
          <div className="flex items-center gap-1">
            <button onClick={() => handleSaveEdit(fieldName)} className="text-green-600 hover:text-green-800 p-1" title="Save">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </button>
            <button onClick={handleCancelEdit} className="text-red-600 hover:text-red-800 p-1" title="Cancel">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between w-full">
        <span
          className={
            currentValue && currentValue !== "-"
              ? "text-sm text-gray-900"
              : "text-[13px] text-gray-500 italic"
          }
        >
          {currentValue && currentValue !== "-"
            ? currentValue
            : `Select`}
        </span>
        <div className="flex items-center gap-1">
          {/* Files buttons for specific rows */}
          {fieldName === "contract" && (
            <button
              onClick={() => setIsFilesOpen(true)}
              className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100"
              title="Open Contract Files"
            >
              {hasContractFiles ? <PaperWithClipIcon /> : <PaperClipIcon />}
            </button>
          )}
          {fieldName === "taxExempt" && (
            <button
              onClick={() => setIsTaxExemptOpen(true)}
              className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100"
              title="Open Tax Exempt Files"
            >
              {hasTaxExemptFiles ? <PaperWithClipIcon /> : <PaperClipIcon />}
            </button>
          )}
          {fieldName === "ppBonds" && (
            <button
              onClick={() => setIsPPBondsOpen(true)}
              className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100"
              title="Open P&P Bonds Files"
            >
              {hasPPBondsFiles ? <PaperWithClipIcon /> : <PaperClipIcon />}
            </button>
          )}
          {fieldName === "prevailingWage" && (
            <button
              onClick={() => setIsPrevailingWageOpen(true)}
              className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100"
              title="Open Prevailing Wage Files"
            >
              {hasPrevailingWageFiles ? <PaperWithClipIcon /> : <PaperClipIcon />}
            </button>
          )}

          <button
            onClick={() => handleEditClick(fieldName)}
            className="text-gray-400 hover:text-gray-600 p-1"
            title="Edit"
          >
            <PencilIcon />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col px-4 py-2 text-gray-600">
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="text-sm font-medium">Customer:</div>
          <button onClick={() => setIsEditOpen(true)} className="text-sm text-red-700 hover:underline">
            Edit
          </button>
        </div>
        <div className="text-base text-gray-900 truncate" title={projectData.customerName || '-'}>
          {projectData.customerName || '-'}
        </div>
        <div className="text-base text-gray-900">{formatCustomerAddress()}</div>
      </div>

      <div className="mb-4">
        <div className="text-sm font-medium mb-1">Project Address:</div>
        <div className="text-base text-gray-900">{formatAddress()}</div>
        <button
          onClick={() => {
            const street = projectData.street || '';
            const city = projectData.city || '';
            const state = projectData.state || '';
            const zipCode = projectData.zipCode || '';
            if (city || state) {
              const addr = [street, city, state, zipCode].filter(Boolean).join(", ");
              window.open(`https://maps.google.com/?q=${encodeURIComponent(addr)}`, '_blank');
            } else if (projectData.projectAddress && projectData.projectAddress !== '-') {
              window.open(`https://maps.google.com/?q=${encodeURIComponent(projectData.projectAddress)}`, '_blank');
            }
          }}
          className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 text-sm text-white bg-red-700 rounded hover:bg-red-800 transition-colors"
        >
          <img src={Icon22} className="w-4 h-4" alt="Map Icon" />
          <span>Map</span>
        </button>
      </div>

      <div>
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-700">Contract</span>
          </div>
          <div className="contact-tab-item ml-4">
            <div className="tab-content flex justify-between items-center w-full">
              <div className="w-full">
                {renderEditableField('contract', 'Contract', ["Executed Proposal", "Executed", "Received LOI", "Signed Proposal", "Required", "Sent", "Service"], getFieldValue(projectData, 'contract', 'contract'))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-700">Tax Exempt</span>
          </div>
          <div className="contact-tab-item ml-4">
            <div className="tab-content flex justify-between items-center w-full">
              <div className="w-full">
                {renderEditableField('taxExempt', 'Tax Exempt', ["Taxable", "Req'd Not Received", "Received", "Required"], getFieldValue(projectData, 'taxExempt', 'tax_exempt'))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-700">P&P Bonds</span>
          </div>
          <div className="contact-tab-item ml-4">
            <div className="tab-content flex justify-between items-center w-full">
              <div className="w-full">
                {renderEditableField('ppBonds', 'P&P Bonds', ["Yes", "No"], getFieldValue(projectData, 'ppBonds', 'p_and_p_bonds'))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-700">Prevailing Wage</span>
          </div>
          <div className="contact-tab-item ml-4">
            <div className="tab-content flex justify-between items-center w-full">
              <div className="w-full">
                {renderEditableField('prevailingWage', 'Prevailing Wage', ["Yes", "No"], getFieldValue(projectData, 'prevailingWage', 'prevailing_wage'))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-700">Certified Payroll</span>
          </div>
          <div className="contact-tab-item ml-4">
            <div className="tab-content flex justify-between items-center w-full">
              <div className="w-full">
                {renderEditableField('certifiedPayroll', 'Certified Payroll', ["Yes", "No"], getFieldValue(projectData, 'certifiedPayroll', 'certified_payroll'))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-700">CCIP</span>
          </div>
          <div className="contact-tab-item ml-4">
            <div className="tab-content flex justify-between items-center w-full">
              <div className="w-full">
                {renderEditableField('ccip', 'CCIP', ["Yes", "No"], getFieldValue(projectData, 'ccip', 'ccip'))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-700">Retainage</span>
          </div>
          <div className="contact-tab-item ml-4">
            <div className="tab-content flex justify-between items-center w-full">
              <div className="w-full">
                {renderEditableField('retainage', 'Retainage', ["0%", "5%", "10%"], getFieldValue(projectData, 'retainage', 'retainage'))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-700">Billing Date</span>
          </div>
          <div className="contact-tab-item ml-4">
            <div className="tab-content flex justify-between items-center w-full">
              <div className="w-full">
                {renderEditableField('billingDate', 'Billing Date', ["10th", "15th", "20th", "25th", "30th", "Upon Completion"], getFieldValue(projectData, 'billingDate', 'bill_date'))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isEditOpen && (
        <EditProjectModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          initialData={projectData}
        />
      )}

      {isFilesOpen && (
        <ContractFilesModal
          isOpen={isFilesOpen}
          onClose={() => setIsFilesOpen(false)}
          projectData={projectData}
        />
      )}

      {isTaxExemptOpen && (
        <LibraryFilesModal
          isOpen={isTaxExemptOpen}
          onClose={() => setIsTaxExemptOpen(false)}
          projectData={projectData}
          libraryName="Compliance"
          subPath="Tax Exempt"
        />
      )}

      {isPrevailingWageOpen && (
        <LibraryFilesModal
          isOpen={isPrevailingWageOpen}
          onClose={() => setIsPrevailingWageOpen(false)}
          projectData={projectData}
          libraryName="Compliance"
          subPath="Prevailing Wage"
        />
      )}

      {isPPBondsOpen && (
        <LibraryFilesModal
          isOpen={isPPBondsOpen}
          onClose={() => setIsPPBondsOpen(false)}
          projectData={projectData}
          libraryName="Insurance & Bonds"
          subPath="P&P Bonds"
        />
      )}
    </div>
  );
}

export default ProjectInfo;
